/** The creator prototype's eight silhouettes, in a 200-unit box. Native clients generate
 * resources from this module; the existing library character/storage contract is unchanged. */
type Point = { x: number; y: number };
const COUNT = 56;
type Cubic = readonly [number, number, number, number, number, number];

function outline(start: readonly [number, number], curves: readonly Cubic[]): Point[] {
  const dense: Point[] = [{ x: start[0], y: start[1] }];
  let previous = dense[0]!;
  for (const [ax, ay, bx, by, x, y] of curves) {
    for (let j = 1; j <= 160; j++) {
      const t = j / 160;
      const u = 1 - t;
      dense.push({
        x: u ** 3 * previous.x + 3 * u * u * t * ax + 3 * u * t * t * bx + t ** 3 * x,
        y: u ** 3 * previous.y + 3 * u * u * t * ay + 3 * u * t * t * by + t ** 3 * y,
      });
    }
    previous = { x, y };
  }
  dense.push(dense[0]!);
  const lengths = [0];
  for (let i = 1; i < dense.length; i++) {
    const a = dense[i - 1]!;
    const b = dense[i]!;
    lengths.push(lengths[i - 1]! + Math.hypot(b.x - a.x, b.y - a.y));
  }
  const perimeter = lengths.at(-1)!;
  let segment = 1;
  const samples = Array.from({ length: 320 }, (_, i) => {
    const distance = (i / 320) * perimeter;
    while (segment < lengths.length - 1 && lengths[segment]! < distance) segment++;
    const a = dense[segment - 1]!;
    const b = dense[segment]!;
    const t =
      (distance - lengths[segment - 1]!) /
      Math.max(1e-9, lengths[segment]! - lengths[segment - 1]!);
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
  });
  const area = samples.reduce((sum, p, i) => {
    const next = samples[(i + 1) % samples.length]!;
    return sum + p.x * next.y - next.x * p.y;
  }, 0);
  if (area < 0) samples.reverse();
  let first = 0;
  let best = Infinity;
  samples.forEach((p, i) => {
    const score = Math.abs(p.x - 100) + (p.y - 30) * 0.15;
    if (p.y < 100 && score < best) {
      best = score;
      first = i;
    }
  });
  return Array.from(
    { length: COUNT },
    (_, i) => samples[(first + Math.floor((i / COUNT) * 320)) % 320]!,
  );
}

function polar(fn: (theta: number) => Point): Point[] {
  return Array.from({ length: COUNT }, (_, i) => {
    const p = fn(-Math.PI / 2 + (i / COUNT) * Math.PI * 2);
    return { x: 100 + p.x, y: 100 + p.y };
  });
}

export const ORGANIC_SHAPES = [
  {
    id: "cloud",
    name: "蓬松云朵",
    eyeY: 102,
    eyeGap: 15,
    tilt: -10,
    hasStem: false,
    points: outline(
      [62, 156],
      [
        [36, 156, 20, 141, 20, 119],
        [20, 100, 32, 85, 49, 82],
        [52, 53, 74, 34, 102, 34],
        [129, 34, 150, 52, 154, 78],
        [170, 81, 181, 96, 181, 116],
        [181, 140, 164, 156, 138, 156],
      ],
    ),
  },
  {
    id: "blob",
    name: "果冻胖团",
    eyeY: 99,
    eyeGap: 15,
    tilt: -12,
    hasStem: false,
    points: polar((t) => {
      const r =
        68 + 3 * Math.cos(2 * t - 0.35) + 2.4 * Math.sin(3 * t + 0.5) - 1.4 * Math.cos(4 * t);
      return { x: Math.cos(t) * r * 1.04, y: Math.sin(t) * r * 0.97 };
    }),
  },
  {
    id: "teardrop",
    name: "软萌水滴",
    eyeY: 112,
    eyeGap: 14.5,
    tilt: -10,
    hasStem: false,
    points: outline(
      [100, 26],
      [
        [114, 26, 166, 84, 166, 124],
        [166, 155, 138, 174, 100, 174],
        [62, 174, 34, 155, 34, 124],
        [34, 84, 86, 26, 100, 26],
      ],
    ),
  },
  {
    id: "heart",
    name: "果冻心形",
    eyeY: 96,
    eyeGap: 15.5,
    tilt: -10,
    hasStem: false,
    points: outline(
      [100, 54],
      [
        [110, 36, 132, 28, 152, 36],
        [174, 45, 180, 74, 166, 104],
        [152, 134, 118, 164, 100, 170],
        [82, 164, 48, 134, 34, 104],
        [20, 74, 26, 45, 48, 36],
        [68, 28, 90, 36, 100, 54],
      ],
    ),
  },
  {
    id: "pillow",
    name: "充气软枕",
    eyeY: 99,
    eyeGap: 15,
    tilt: -12,
    hasStem: false,
    points: polar((t) => {
      const c = Math.cos(t),
        s = Math.sin(t),
        puff = 1 - 0.04 * Math.cos(4 * t);
      return {
        x: Math.sign(c) * Math.abs(c) ** 0.6 * 68 * puff,
        y: Math.sign(s) * Math.abs(s) ** 0.6 * 66 * puff,
      };
    }),
  },
  {
    id: "pudding",
    name: "麻薯布丁",
    eyeY: 102,
    eyeGap: 14.5,
    tilt: -10,
    hasStem: false,
    points: outline(
      [100, 30],
      [
        [132, 30, 144, 58, 154, 92],
        [164, 124, 174, 142, 162, 158],
        [150, 171, 126, 171, 100, 171],
        [74, 171, 50, 171, 38, 158],
        [26, 142, 36, 124, 46, 92],
        [56, 58, 68, 30, 100, 30],
      ],
    ),
  },
  {
    id: "star",
    name: "五瓣星糖",
    eyeY: 101,
    eyeGap: 14.5,
    tilt: -8,
    hasStem: false,
    points: polar((t) => {
      const r = 63 + 10 * Math.cos(5 * (t + Math.PI / 2)) + 1.6 * Math.cos(10 * (t + Math.PI / 2));
      return { x: Math.cos(t) * r, y: Math.sin(t) * r };
    }),
  },
  {
    id: "pumpkin",
    name: "软萌南瓜",
    eyeY: 104,
    eyeGap: 15.5,
    tilt: -10,
    hasStem: true,
    points: polar((t) => {
      const rib = Math.abs(Math.sin(4 * (t + Math.PI / 2))) ** 0.65;
      const rx = 72 - (1 - rib) * 5.2,
        ry = 63 - (1 - rib) * 4.8 - Math.sin(t) ** 4 * 5.5;
      return { x: Math.cos(t) * rx, y: Math.sin(t) * ry + 4 };
    }),
  },
] as const;

export type OrganicShape = (typeof ORGANIC_SHAPES)[number];
export type OrganicShapeId = OrganicShape["id"];

export const ORGANIC_SHAPE_COUNT = ORGANIC_SHAPES.length;
export const DEFAULT_ORGANIC_SHAPE_INDEX = 0; // cloud
export const DEFAULT_ORGANIC_COLOR = "#FF2D79"; // Barbie pink

/**
 * Catmull-Rom spline converting 56 points to a closed cubic Bezier path in a 200x200 box.
 * Matches OrganicBotPainter.outline in iOS.
 */
export function organicShapeToBezierPath(
  points: readonly { x: number; y: number }[],
): string {
  if (!points || points.length < 3) return "";
  const n = points.length;
  let d = `M ${points[0]!.x.toFixed(2)} ${points[0]!.y.toFixed(2)}`;
  for (let i = 0; i < n; i++) {
    const p0 = points[(i - 1 + n) % n]!;
    const p1 = points[i]!;
    const p2 = points[(i + 1) % n]!;
    const p3 = points[(i + 2) % n]!;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`;
  }
  return `${d} Z`;
}

/** Precomputed SVG Bezier path string for each of the 8 shapes. */
export const ORGANIC_SHAPE_PATHS = ORGANIC_SHAPES.map((shape) =>
  organicShapeToBezierPath(shape.points),
);

/** Pumpkin stem path matching iOS OrganicBotPainter.stem. */
export const ORGANIC_STEM_PATH =
  "M -5 4 C -6 -8, -10 -18, -15 -24 C -17 -27, -12 -31, -6 -29 C 2 -26, 6 -12, 7 4 Z";

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Generates eye path with dynamic smile & blink interpolation.
 * smile = 0: capsule eye (I I)
 * smile = 1: happy moon smile (∩ ∩)
 * Matches OrganicEyePath in iOS and buildEyePath in ui/bot.html.
 */
export function buildOrganicEyePath(
  cx: number,
  cy: number,
  smile = 0,
  blink = 1.0,
  scale = 1.05,
): string {
  const s = Math.max(0, Math.min(1, smile));
  const effectiveBlink = lerp(blink, Math.max(0.75, blink), s);
  const sy = Math.max(0.08, effectiveBlink) * scale;
  const sx = (1.0 + (1.0 - effectiveBlink) * 0.15) * scale;

  const pt = (x: number, y: number) =>
    `${(cx + x * sx).toFixed(2)} ${(cy + y * sy).toFixed(2)}`;

  const a0y = lerp(-13.2, -10.8, s);
  const a0h = lerp(3.3, 8.2, s);

  const a1x = lerp(5.8, 12.6, s);
  const a1y = lerp(-6.8, 1.2, s);
  const a1InX = lerp(5.8, 12.6, s);
  const a1InY = lerp(-10.6, -6.2, s);
  const a1OutX = lerp(5.8, 12.6, s);
  const a1OutY = lerp(0.0, 4.6, s);

  const a2x = lerp(5.8, 3.3, s);
  const a2y = lerp(6.8, 1.8, s);
  const a2InX = lerp(5.8, 11.4, s);
  const a2InY = lerp(0.0, 7.6, s);
  const a2OutX = lerp(5.8, 3.1, s);
  const a2OutY = lerp(10.6, -0.8, s);

  const a3y = lerp(13.2, -1.8, s);
  const a3h = lerp(3.3, 2.0, s);

  const a4x = -a2x;
  const a4y = a2y;
  const a4InX = -a2OutX;
  const a4InY = a2OutY;
  const a4OutX = -a2InX;
  const a4OutY = a2InY;

  const a5x = -a1x;
  const a5y = a1y;
  const a5InX = -a1OutX;
  const a5InY = a1OutY;
  const a5OutX = -a1InX;
  const a5OutY = a1InY;

  return (
    `M ${pt(0, a0y)} ` +
    `C ${pt(a0h, a0y)}, ${pt(a1InX, a1InY)}, ${pt(a1x, a1y)} ` +
    `C ${pt(a1OutX, a1OutY)}, ${pt(a2InX, a2InY)}, ${pt(a2x, a2y)} ` +
    `C ${pt(a2OutX, a2OutY)}, ${pt(a3h, a3y)}, ${pt(0, a3y)} ` +
    `C ${pt(-a3h, a3y)}, ${pt(a4InX, a4InY)}, ${pt(a4x, a4y)} ` +
    `C ${pt(a4OutX, a4OutY)}, ${pt(a5InX, a5InY)}, ${pt(a5x, a5y)} ` +
    `C ${pt(a5OutX, a5OutY)}, ${pt(-a0h, a0y)}, ${pt(0, a0y)} Z`
  );
}

/**
 * Calculates eye color based on luminance (> 0.72 -> #1C1C1E, else #FFFFFF).
 * Matches OrganicPalette.eyes in iOS.
 */
export function organicEyeColor(hex: string): string {
  const match = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(hex);
  if (!match) return "#FFFFFF";
  let h = match[1]!;
  if (h.length === 3) h = [...h].map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const luminance = r * 0.2126 + g * 0.7152 + b * 0.0722;
  return luminance > 0.72 ? "#1C1C1E" : "#FFFFFF";
}

/**
 * FNV-1a 32-bit hash on UTF-16 code units.
 * Matches LibraryAvatar.hash in iOS.
 */
export function fnv1a32(text: string): number {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/**
 * Extracts shape index from `#HEX::shape_N` or computes deterministic fallback via FNV-1a.
 */
export function parseOrganicShapeIndex(rawColor: string, identity = ""): number {
  if (rawColor && rawColor.includes("::shape_")) {
    const parts = rawColor.split("::shape_");
    const rawShape = parts[1] ?? "0";
    if (/^\d+$/.test(rawShape)) {
      const parsed = Number(rawShape);
      if (Number.isSafeInteger(parsed)) {
        return ((parsed % ORGANIC_SHAPE_COUNT) + ORGANIC_SHAPE_COUNT) % ORGANIC_SHAPE_COUNT;
      }
    }
    return 0;
  }
  const key = identity.length > 0 ? identity : rawColor || "agent";
  return fnv1a32(key) % ORGANIC_SHAPE_COUNT;
}

export type OrganicAvatarResolution = {
  kind: "shape" | "image";
  shapeIndex: number;
  shape: OrganicShape;
  color: string;
  eyeColor: string;
  imageUrl?: string;
  tilt: number;
  d: string;
};

/**
 * Resolves a stored bot color into an organic shape, colors, and SVG paths.
 */
export function resolveOrganicAvatar(
  rawColor: string,
  identity = "",
): OrganicAvatarResolution {
  const shapeIndex = parseOrganicShapeIndex(rawColor, identity);
  const shape = ORGANIC_SHAPES[shapeIndex] ?? ORGANIC_SHAPES[0]!;
  const d = ORGANIC_SHAPE_PATHS[shapeIndex] ?? organicShapeToBezierPath(shape.points);

  if (rawColor && rawColor.startsWith("data:image/")) {
    return {
      kind: "image",
      shapeIndex,
      shape,
      color: DEFAULT_ORGANIC_COLOR,
      eyeColor: "#FFFFFF",
      imageUrl: rawColor,
      tilt: shape.tilt,
      d,
    };
  }

  const colorPart = (rawColor || "").split("::shape_")[0] || "";
  const color = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(colorPart)
    ? colorPart
    : DEFAULT_ORGANIC_COLOR;
  const eyeColor = organicEyeColor(color);

  return {
    kind: "shape",
    shapeIndex,
    shape,
    color,
    eyeColor,
    tilt: shape.tilt,
    d,
  };
}

/**
 * Combines a hex color and an organic shape index into `#HEX::shape_N`.
 */
export function withOrganicShape(color: string, shapeIndex: number): string {
  const baseColor = (color || "").split("::shape_")[0] || DEFAULT_ORGANIC_COLOR;
  const validIndex =
    ((Math.trunc(shapeIndex) % ORGANIC_SHAPE_COUNT) + ORGANIC_SHAPE_COUNT) % ORGANIC_SHAPE_COUNT;
  return `${baseColor}::shape_${validIndex}`;
}

